import clone from 'rfdc';
import {
  Component,
  ContentType,
  Field,
  VersionStatus,
} from 'contensis-core-api';
import { Entry, Node, Tag, TagGroup } from 'contensis-management-api';
import { FormContentType } from '~/models/FormContentTypeManagement';
import ContensisRepository from '~/repositories/ContensisRepository';
import { diffStrings, jsonIsEqual } from '~/util/diff';
import { deepClean, sortObjectKeysDeep } from '~/util/object';
import { isArray, isNullOrUndefined } from '~/util/typeguards';
import { NulledEntry, WebhookSubscription } from '~/models';

export const compareFields = (
  target:
    | NulledEntry
    | Node
    | ContentType
    | Component
    | FormContentType
    | Tag
    | TagGroup
    | WebhookSubscription,
  source:
    | Entry
    | NulledEntry
    | Node
    | ContentType
    | Component
    | FormContentType
    | Tag
    | TagGroup
    | WebhookSubscription,
  sourceRepo: ContensisRepository,
  targetRepo: ContensisRepository,
  dontCompareFields = [
    // 'sys',
    'contentTypeId',
    'dataFormat',
    'entryThumbnail',
    'entryDescription',
    'entryTitle',
    'transformations',
    'uploadPath',
    // 'uri',
  ]
) => {
  // Checking fields to detect an update

  // Remove all null value fields as these are likely to be inconsistent
  // First we remove all transformations, uploadPath and uri fields regardless of value
  // We run cleaner again to remove all nulls and empty objects
  // Run cleaner once more to remove any empty objects left over by having its null properties removed
  // Finally sort the keys alphabetically to normalise the field order so we can accurately
  // compare each object after being converted to strings
  // --we have stripped out uploadPath to account for potential differences

  // const cloneSource = JSON.parse(JSON.stringify(source));
  // const cloneTarget = JSON.parse(JSON.stringify(target));
  const sourceCleanedSortedKeys = sortObjectKeysDeep(
    deepClean(clone()(source), dontCompareFields)
  );

  const targetCleanedSortedKeys = sortObjectKeysDeep(
    deepClean(clone()(target), dontCompareFields)
  );

  // --needs to be language-aware so we have swapped out the language with the target project's
  const sourceFields = JSON.stringify(sourceCleanedSortedKeys).replace(
    new RegExp(sourceRepo.project?.primaryLanguage || 'en-GB', 'g'),
    targetRepo.project?.primaryLanguage || 'en-GB'
  );

  const targetFields = JSON.stringify(targetCleanedSortedKeys);

  const changed = !jsonIsEqual(targetFields, sourceFields);
  const diff = changed ? diffStrings(targetFields, sourceFields) : undefined;

  return { id: source.id, changed, diff };
};

export const getCmsFilePath = (e: Entry) => {
  if (
    !(e.sys as any)?.properties.filePath ||
    !(e.sys as any)?.properties.filename
  )
    return undefined;
  return `${(e.sys as any)?.properties.filePath}${
    (e.sys as any)?.properties.filename
  }`;
};

/**
 * Boilerplate to resolve a value in an entry by field id
 * @param entry The entry containing the field value to return
 * @param fieldId The id of the entry field to get
 * @returns The resolved field value
 */
export const getFieldValue = (entry: Entry, fieldId: string) => {
  const fieldParts = fieldId.split('.'); // fieldId could be `contentType.component.nested`
  let obj: any = entry;
  // Loop over all field parts and mutate the entry object
  // once the correct/final field part is reached
  for (let i = 0; i < fieldParts.length; i++) {
    const id = fieldParts[i];

    // handle array values by picking the value at pos 0
    if (isArray(obj)) {
      obj = obj?.[0];
    }
    if (!isNullOrUndefined(obj?.[id])) {
      // field exists and more parts to come
      if (fieldParts.length > i + 1) {
        obj = obj[id];
      } else {
        // no more parts to come
        return obj[id];
      }
    } else {
      // field does not exist
      return obj?.[id];
    }
  }
};

export const getSys = (e: Entry) => {
  if (e.asset) return e.asset.sys;
  return e.sys;
};

export const getSysId = (e: Entry) => {
  return getSys(e).id;
};

// take any versionStatus from supplied zenQL and set this in the query so
// we can use versionStatus to trigger behaviours in the app
export const parseZenqlVersionStatus = (zenQL?: string) => {
  if (typeof zenQL === 'string' && zenQL.includes(`sys.versionStatus`)) {
    const versionStatusMatch = zenQL.match(/sys.versionStatus\s*=\s*([\S\s]+)/);
    if (versionStatusMatch?.[1]) {
      const versionStatus = versionStatusMatch[1].replace(
        /[\"']/g,
        ''
      ) as VersionStatus;
      return versionStatus;
    }
  }
  return;
};

export const loadEntryIds = (
  arr: ContensisRepository['entryIds'],
  entries: Entry[]
) => {
  if (Array.isArray(arr))
    for (const e of entries)
      arr.push([
        e.sys.contentTypeId || '',
        e.sys.id || '',
        e.entryTitle,
        e.sys.uri || '',
        (e.sys as any).properties?.filePath,
      ]);
  return arr;
};

export const mergeEntryIds = (
  idArray: ContensisRepository['entryIds'],
  loadedEntryIds: ContensisRepository['entryIds'],
  entryArray: Entry[],
  entries: Entry[]
) => {
  for (const leId of loadedEntryIds || []) {
    const entryId = leId[1];
    if (idArray.findIndex(([, id]) => entryId === id) === -1) {
      idArray.push(leId);
      // perf: for larger payloads "merge" and "dedupe"
      // returned entries into the main "raw" array
      // at the same time as we maintain the entryIds "index"
      const pos = entries.findIndex((e) => e.sys.id === entryId);
      entryArray.push(entries[pos]);
    }
  }
  return idArray;
};

/**
 * transformContentTypeId has limited usability and should
 * be used to move entries if they have been created in the wrong content type, it cannot
 * be used more than once to duplicate an entry in a project due to guid clashes and
 * cannot be used for sync operations.
 * @param entry the entry we are building
 * @param transformContentTypes object containing contentTypeId keys containing the values to transform
 * @returns the entry we are building with contentTypeId transformed if specified
 */
export const transformContentTypeId = (
  entry: Entry,
  transformContentTypes: { [k: string]: string } = {}
) => {
  if (
    entry.sys.contentTypeId &&
    Object.keys(transformContentTypes).includes(entry.sys.contentTypeId)
  ) {
    return {
      ...entry,
      sys: {
        ...entry.sys,
        contentTypeId: transformContentTypes[entry.sys.contentTypeId],
      },
    };
  }
  return entry;
};

/**
 * Boilerplate to resolve a field by id in a target entry and add the value to that field
 * mutating the provided entry argument
 * @param entry The entry containing the field to update
 * @param fieldId The id of the entry field to update
 * @param value The value to update the entry field with
 */
export const updateFieldValue = (
  entry: Entry,
  fieldId: string,
  value: any,
  parentFields?: Field[]
) => {
  const fieldParts = fieldId.split('.'); // fieldId could be `contentType.component.nested`

  // get a handle on the parent field so we can recreate any nested value properly if we cannot infer from an existing value
  const isArrayFieldAtLevel = (i: number) =>
    parentFields?.[i]?.dataType.endsWith('Array');

  const newContainer = (i: number) => (isArrayFieldAtLevel(i) ? [] : {});

  const { isArray } = Array;

  // the object that contains the existing value at this field level
  // we will mutate and reassign this at each level in the loop
  let container: any = entry;

  // Loop over all field parts and mutate the entry object
  // once the correct/final field part is reached
  for (let fieldIndex = 0; fieldIndex < fieldParts.length; fieldIndex++) {
    const fieldId = fieldParts[fieldIndex];

    const hasMoreFieldParts = fieldParts.length > fieldIndex + 1;

    if (!isNullOrUndefined(container?.[fieldId])) {
      // field exists in the container
      if (hasMoreFieldParts) {
        // reassign container for next level
        container = container[fieldId];
      } else {
        // no more parts to come
        if (isArray(container)) {
          if (container[0] && typeof container[0] === 'object')
            container[0][fieldId] = value;
          // add a new item to an existing array
          else container.push({ [fieldId]: value });
        }
        if (Array.isArray(container[fieldId]) && !isArray(value)) {
          if (container[fieldId]?.[0]) {
            container[fieldId][0] = value;
          } else container[fieldId].push(value);
        } else container[fieldId] = value;
      }
    } else {
      // field part is held inside an array
      if (isArray(container)) {
        if (hasMoreFieldParts) {
          if (container[0] === null) {
            // we sometimes see existing repeatable field values like [null]
            container[0] = { [fieldId]: newContainer(fieldIndex) };
          }
          // does anything exist at pos 0
          else if (container[0] && typeof container[0] === 'object') {
            // does anything exist at pos 0 with a key of our id
            // if not initialise the key with an empty object
            if (
              container[0][fieldId] &&
              typeof container[0][fieldId] !== 'object'
            )
              container[0][fieldId] = {};
          } else {
            // don't update the entry at this level for a null or undefined value
            if (isNullOrUndefined(value) || value === '') return;
            // nothing exists in the array at this level
            // push a new item initialising our key
            container.push({ [fieldId]: newContainer(fieldIndex) });
          }
          container = container[0][fieldId];
        } else {
          // no more parts to come
          if (container[0] === null) {
            // we sometimes see existing repeatable field values like [null]
            container[0] = { [fieldId]: newContainer(fieldIndex) };
          }
          if (container[0] && typeof container[0] === 'object')
            // if any object exists at pos 0 of the array
            // add the key and the value to this object
            container[0][fieldId] = value;
          // push a new item with our key and the value
          else container.push({ [fieldId]: value });
        }
      } else {
        // field container is not an array
        if (hasMoreFieldParts) {
          if (isNullOrUndefined(container[fieldId])) {
            // checks parent field for what this should be
            container[fieldId] = newContainer(fieldIndex);
          }
          container = container[fieldId];
        }
        // no more parts to come
        else container[fieldId] = value;
      }
    }
  }
};
